<template>
  <div class="pt-2 pb-6 py-5 md:mx-8 mx-4">
    <!--
      La tarjeta de estado de acceso (borrador/publicado/privado…) estaba oculta y sus botones
      no hacían nada; se ha retirado hasta que exista el backend (LMS-052/053).
    -->
    <div class="bg-white shadow rounded-lg divide-y divide-gray-200">
      <div class="p-6">
        <h3 class="text-lg font-semibold leading-5 text-gray-900 sm:truncate">{{ $t('projects.enrol_configuration') }}</h3>
      </div>

      <div v-if="!hasConfiguration && !showConfiguration" class="px-6">
        <alert-component class="mt-8">
          {{ $t('projects.enrol_configuration_empty') }}
        </alert-component>
        <div class="flex justify-center py-8">
          <button type="button" v-on:click="showConfiguration = true"
                  class="inline-flex items-center shadow-sm px-4 py-1.5 transition ease-in border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-brainspro-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
            {{ $t('projects.enrol_configuration_create') }}
          </button>
        </div>
      </div>

      <div v-else class="px-6">
        <alert-component class="mt-8">
          {{ $t('projects.project_expiration_info') }}
        </alert-component>

        <div class="flex flex-col lg:flex-row w-full gap-8">
          <fieldset v-for="field in fields" :key="field.key" class="basis-1/2 pt-6 pb-4">
            <legend class="block text-gray-600 text-sm font-bold mb-2">{{ $t(field.label) }}</legend>
            <div class="flex gap-4">
              <label :for="field.key + '_value'" class="sr-only">{{ $t('projects.enrol_time_value') }}</label>
              <input :id="field.key + '_value'" v-model.number="configuration[field.key + '_value']" type="number" min="0" step="1" required
                     class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5">
              <label :for="field.key + '_unit'" class="sr-only">{{ $t('projects.enrol_time_unit') }}</label>
              <select :id="field.key + '_unit'" v-model="configuration[field.key + '_unit']" required
                      class="form-select block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white border border-solid border-gray-300 rounded focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none">
                <option v-for="unit in units" :key="unit" :value="unit">{{ $t('projects.time_unit_' + unit) }}</option>
              </select>
            </div>
          </fieldset>
        </div>
        <p v-if="error" class="pb-4 text-sm text-red-600">{{ error }}</p>
      </div>

      <div v-if="hasConfiguration || showConfiguration" class="px-6 py-4 flex justify-end">
        <button v-if="hasConfiguration" type="button" v-on:click="deleteEnrolConfiguration" :disabled="general.loading"
                class="mr-2 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-red-500 bg-white hover:bg-red-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
          {{ $t('projects.project_delete') }}
        </button>
        <button v-else type="button" v-on:click="cancelEnrolConfiguration" :disabled="general.loading"
                class="mr-2 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
          {{ $t('general.cancel') }}
        </button>
        <button type="button" v-on:click="saveEnrolConfiguration" :disabled="general.loading"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-bpbluedeep disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
          <font-awesome-icon v-if="general.loading" class="mr-2 h-4 w-4 animate-spin" icon="spinner"/>
          {{ $t('general.save') }}
        </button>
      </div>
    </div>

    <unsaved-changes-modal :open="isLeaveConfirmationOpen" @leave="confirmLeave" @stay="cancelLeave"></unsaved-changes-modal>
  </div>
</template>

<script>
import AlertComponent from "@/components/feedback/alerts/AlertComponent";
import {useProjectsStore} from "@/stores/projects";
import ProjectService from "@/services/project/ProjectService";
import NotificationService from "@/services/notification/NotificationService";
import UnsavedChangesModal from "@/components/modals/UnsavedChangesModal";
import {useUnsavedChanges} from "@/composables/useUnsavedChanges";
import {ref} from "vue";

// Unidades admitidas por el backend (enum de project_enrol_configurations).
const UNITS = ['hours', 'days', 'weeks', 'months', 'years']
const CONFIGURATION_FIELDS = ['time_start_value', 'time_start_unit', 'time_end_value', 'time_end_unit']
const pickConfiguration = (configuration) => Object.fromEntries(CONFIGURATION_FIELDS.map((field) => [field, configuration?.[field]]))

export default {
  name: "ProjectAccessComponent",
  components: {AlertComponent, UnsavedChangesModal},
  data() {
    return {
      general: {
        loading: false,
      },
      showConfiguration: false,
      error: '',
      // Última configuración guardada, para detectar cambios sin guardar (FE-032).
      savedConfiguration: null,
      units: UNITS,
      fields: [
        {key: 'time_start', label: 'projects.enrol_time_start'},
        {key: 'time_end', label: 'projects.enrol_time_end'},
      ],
    }
  },
  setup() {
    const projectStore = useProjectsStore()
    // Se sincroniza con el computed `isDirty` mediante un watcher.
    const hasUnsavedChanges = ref(false)

    return {
      projectStore,
      hasUnsavedChanges,
      ...useUnsavedChanges(hasUnsavedChanges)
    }
  },
  computed: {
    configuration() {
      return this.projectStore.project.enrol_configuration
    },
    hasConfiguration() {
      return this.configuration.time_start_value !== 0 || this.configuration.time_end_value !== 0
    },
    isDirty() {
      return this.savedConfiguration !== null
          && JSON.stringify(pickConfiguration(this.configuration)) !== JSON.stringify(this.savedConfiguration)
    }
  },
  watch: {
    // El store sustituye el objeto al cargar el proyecto: ese es el estado guardado.
    configuration: {
      handler() {
        this.snapshotConfiguration()
      },
      immediate: true
    },
    isDirty: {
      handler(isDirty) {
        this.hasUnsavedChanges = isDirty
      },
      immediate: true
    }
  },
  beforeUnmount() {
    // Si se sale descartando cambios, el store vuelve a la configuración guardada.
    if (this.isDirty) Object.assign(this.configuration, this.savedConfiguration)
  },
  methods: {
    snapshotConfiguration() {
      this.savedConfiguration = pickConfiguration(this.configuration)
    },
    cancelEnrolConfiguration() {
      Object.assign(this.configuration, this.savedConfiguration)
      this.showConfiguration = false
      this.error = ''
    },
    validate() {
      const values = [this.configuration.time_start_value, this.configuration.time_end_value]
      if (values.some((value) => !Number.isInteger(value) || value < 0)) {
        return this.$t('projects.enrol_configuration_invalid_value')
      }
      if (!UNITS.includes(this.configuration.time_start_unit) || !UNITS.includes(this.configuration.time_end_unit)) {
        return this.$t('projects.enrol_configuration_invalid_unit')
      }
      return ''
    },
    saveEnrolConfiguration() {
      this.error = this.validate()
      if (this.error) return

      this.general.loading = true
      ProjectService.postEnrolConfiguration(this.$route.params.id, this.configuration)
          .then(() => {
            this.snapshotConfiguration()
            NotificationService.showNotification(this.$t('projects.enrol_configuration_saved'), "success")
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.general.loading = false
          })
    },
    deleteEnrolConfiguration() {
      this.general.loading = true
      ProjectService.deleteEnrolConfiguration(this.$route.params.id)
          .then(() => {
            Object.assign(this.configuration, {
              time_start_value: 0,
              time_start_unit: 'hours',
              time_end_value: 0,
              time_end_unit: 'hours',
            })
            this.showConfiguration = false
            this.snapshotConfiguration()
            NotificationService.showNotification(this.$t('projects.enrol_configuration_deleted'), "success")
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.general.loading = false
          })
    },
  }
}
</script>
